// This is a part of the Cadcorp SIS source code. 
// Copyright (c) Cadcorp Limited 1991-2025 
import { SchemaColumn } from './SchemaColumn';
import { IAjax, ITheme, IEventSource, ISchemaColumn, INewTheme, IQueryHelper } from '../../../../CommonScripts/Abstractions';
import { QueryHelper } from './QueryHelper';
import { JoinedOverlayQueryHelper } from './JoinedQueryHelper';
import { Theme } from './Theme';
import { Events } from '../Core/Cadcorp.Web.Core.Events';


/**
* Represents an overlay served from the GeognoSIS http interface
* @public
*/
export class Overlay implements IEventSource {
    /**
    * @public
    * @property {string} name The name of this overlay
    */
    public name: string;
    private m_visibility: boolean;
    /**
    * @export
    * @property {boolean} visibility The visibility of this overlay, true if this overlay is visible otherwise false
    */
    get visibility(): boolean { return this.m_visibility; }

    private m_status: LayerStatus;
    /**
    * @export
    * @property {LayerStatus} status The status of the layer.
    */
    public get status(): LayerStatus { return this.m_status; }

    /**
    * @property {object} properties - Map of overlay properties
    * @export
    */
    public properties: object;
    /**
    * @property {Array.<Object>} themes - Array of themes on this overlay
    * @private
    */
    private m_themes: ITheme[];

    /**
    * @property {Array.<Cadcorp.Core.GeognoSIS.SchemaColumn|null} schema - The schema columns for this overlay
    * @export
    */
    public schema: ISchemaColumn[];
    private m_overlayIndex: number;
    /**
    * @property {number} overlayIndex - The position in the list of overlays
    * @export
    */
    public get overlayIndex(): number { return this.m_overlayIndex; }
    /**
    * @property {number} overlayIndex - The position in the list of overlays
    * @export
    */
    public set overlayIndex(newIndex: number) { this.m_overlayIndex = newIndex; }

    /**
    * @property {number} scaleMin - The minimum scale threashold for this overlay
    * @export
    * @readonly
    */
    public scaleMin: number;

    /**
    * @property {number} scaleMax - The maximum scale threashold for this overlay
    * @export
    * @readonly
    */
    public scaleMax: number;

    /**
    * @property {string|null} abstractText Abstract text for this overlay.
    * @export
    * @readonly
    */
    public abstractText: string;

    /**
    * @property {string|null} userAdded Did the user add this layer.
    * @export
    * @readonly
    */
    public userAdded: boolean;
    /**
    * @property {string} epsgCode The epsg code for this layer
    * @export
    */
    public epsgCode: number;
    /**
    * @property {bool} errorCondition Has there been an error getting this overlay
    * @export
    */
    public errorCondition: boolean;

    /**
    * @property {LayerStatus} maxStatus - The maximum status this layer be set to when toggling the layer on and off. Will be one of the Cadcorp.Web.Entities.GeognoSIS.Constants.LayerStatus values
    * @readonly
    * @export
    */
    public maxStatus: LayerStatus;

    /**
    * @property {boolean} ignoreLayer True if this should be omitted from the layers dialog and feature info requests
    * @readonly
    * @export
    */
    public ignoreLayer: boolean;

    /**
     * @property {string|null} metaDataUUID The identifier for metadata in a CSW
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Overlay#
     * @export
     * @readonly
     */
    public metadataUuid: string;
    /**
     * @property {string|null} sessionId The current session id
     * @export
     * @readonly
     */
    public readonly sessionId: string;
    /**
     * @property {string|null} baseUrl The base url for Geognosis
     * @export
     * @readonly
     */
    public readonly baseUrl: string;
    /**
     * @property {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @public
     */
    public readonly ajaxWrapper: IAjax;
    /**
     * @property {string} layerId The unique id for the swd this layer is in
     * @public
     */
    public readonly layerId: string;
    /**
     * @property {string} mapName The name of the map
     * @public
     */
    public readonly mapName: string;
    /**
    * @property {boolean} useVector Should the layer be rendered using vectors
    * @public
    */
    public useVector: boolean;
    /**
    * @property {string} vectorStyle Style to be applied for vectors
    * @public
    */
    public vectorStyle: string;
    /**
    * @property {string} attribution The attribution for the layer
    * @public
    */
    public attribution: string;
    /**
    * @property {number} refreshInterval Refresh interval, seconds, applied to Geognosis layers with WML::RefreshInterval
    * @public
    */
    public refreshInterval: number;
    /**
    * @property {Cadcorp.Core.Events} m_events The position in the list of overlays
    * @private
    */
    private m_events: Events;

    /**
    * Initialises a new instance of Overlay
    * @constructor
    * @export
    * @param {Object} config Configuration for this object. The config supplied directly extends the control
    */
    constructor(baseUrl: string, sessionId: string, overlayIndex: number, ajaxWrapper: IAjax, layerId: string, mapName: string) {
        this.baseUrl = baseUrl;
        this.sessionId = sessionId;
        this.m_overlayIndex = overlayIndex;
        this.ajaxWrapper = ajaxWrapper;
        this.mapName = mapName;
        this.layerId = layerId;
        this.m_status = null;
        this.scaleMin = null;
        this.scaleMax = null;
        this.abstractText = null;
        this.userAdded = false;
        this.epsgCode = null;
        this.maxStatus = null;
        this.ignoreLayer = false;
        this.metadataUuid = null;
        this.properties = null;
        this.schema = null;
        this.m_events = new Events({ owner: this });
    }
    /**
     * @inheritdoc 
     */
    on(type: string, listener: (eventData: any) => boolean) {
        this.m_events.on(type, listener);
    }
    /**
    * @inheritdoc
    */
    un(type: string, listener: (eventData: any) => boolean) {
        this.m_events.un(type, listener);
    }
    private getSchemaColumnListUrl(): string {
        return this.getSchemaColumnBaseUrl();
    }
    public getSchemaColumnBaseUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlays/{overlayNo:int}/Schema/Columns
        return `${this.getSchemaColumnPropertiesBaseUrl()}s`;
    }
    public getSchemaColumnPropertiesBaseUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlays/{overlayNo:int}/Schema/Column
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}/Schema/Column`;
    }
    public getPropertiesUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlays/{overlayNo:int}
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}`;
    }
    public getDeleteUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlays/{overlayNo:int}
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}`;
    }
    public getImageUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlay/{overlayNo:int}/Image
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}/Image`;
    }
    public getVectorUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlay/{overlayNo:int}/Vector
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}/Vector`;
    }
    public getThemeBaseUrl(): string {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlay/{overlayNo:int}/Vector
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}/themes`;
    }

    public getItemsUrl(): string {
        return `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}/Items`;
    }

    public getQueryUrl(): string {
        return `${this.baseUrl}${this.mapName}/Search/GeognoSis/${this.sessionId}/${this.layerId}/${this.overlayIndex}`;
    }

    public getNextResultsUrls(): string {
        return `${this.baseUrl}${this.mapName}/Search/GeognoSis/${this.sessionId}/${this.layerId}/${this.overlayIndex}/Next`;
    }

    /**
    * Gets a promise to populate the object from the server
    * @param {object} options The options to apply to the ajax request
    * @export
    * @function
    * @returns {Promise} The chainable promise
    */
    public populate(options): Promise<Overlay> {
        const pUrl = this.getPropertiesUrl();
        const p = this.ajaxWrapper.get(pUrl, options).
            then((response: any) => {
                this.setProperties(response.data);
                return this;
            });
        return p;
    }
    /**
     * Removes the overlay from the session
    */
    public remove(): Promise<Overlay> {
        const pUrl = this.getDeleteUrl();
        const p = this.ajaxWrapper.delete(pUrl);
        return p;
    }
    /**
     * Sets the status of this overlay
     * @param {number} newStatus The new overlay status to set, It must be one of the Cadcorp.Web.Entities.GeognoSIS.Constants.LayerStatus constants
     * @public
     * @return {Promise} A promise object to set the status
     */
    public setStatus(newStatus: LayerStatus): Promise<Overlay> {
        let promise = null;
        if (newStatus !== this.status) {
            const url = `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${this.overlayIndex}/Status?newStatus=${newStatus}`;
            promise = this.ajaxWrapper.patch(url, null, null).
                then(() => {
                    this.m_status = newStatus;
                    this.m_visibility = this.m_status > LayerStatus.Invisible;
                    return this;
                });
        }
        else {
            promise = Promise.resolve(this);
        }
        return promise;
    }

    /**
     * Refreshes the overlay so that WebMap can redraw afresh; needed specifically in the case of View Point layers for tracking points and refreshing; vector layers with spatial indexes don't need this step but there is no way to distinguish 
     * @public
     * @return {Promise} A promise object to set the status
     */
    public refreshOverlay(): Promise<void> {        
        const url = `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/RefreshOverlay/${this.overlayIndex}`;
        return this.ajaxWrapper.get(url, null);         
    }

    /**
    * Event handler for get properties success
    * @function
    * @param {Object} returnedData - A data object to populate this overlay from
    * @return {Overlay} This overlay
    */
    public setProperties(returnedData: any) {
        this.name = returnedData.name;
        this.m_status = returnedData.status;
        this.scaleMin = returnedData.scaleMin;
        this.scaleMax = returnedData.scaleMax;
        this.abstractText = returnedData.abstractText;
        this.userAdded = returnedData.userAdded;
        this.useVector = returnedData.useVector;
        this.vectorStyle = returnedData.vectorStyle;
        this.refreshInterval = returnedData.refreshInterval;

        if (returnedData.epsgCode !== 0) {
            this.epsgCode = returnedData.epsgCode;
            this.errorCondition = false;
        }
        else {
            this.epsgCode = null;
            this.errorCondition = true;
        }
        this.maxStatus = returnedData.maxStatus;
        this.ignoreLayer = returnedData.ignoreLayer;
        this.metadataUuid = returnedData.metadataUuid;
        this.properties = returnedData.properties;
        this.attribution = returnedData.attribution;
        this.m_visibility = this.m_status > LayerStatus.Invisible;
        return this;
    }
    /** 
    * @description
    * Gets the schema columns for this layer.<br />
    * If the schema columns haven't been retrieved then they are queried asynchronously. This function returns null immediately and events are raised when each schema column has loaded
    * @export
    * @returns {Promise} A promise that represents an array of schema columns
    */
    public getSchemaColumns(): Promise<ISchemaColumn[]> {
        let retVal = null;
        if (this.schema === null) {
            const url = this.getSchemaColumnListUrl();
            retVal = this.ajaxWrapper.get(url, null).
                then(this.processSchemaList.bind(this)).
                then(this.processSchemaData.bind(this));
        }
        else {
            retVal = Promise.resolve(this.schema);
        }
        return retVal;
    }
    private processSchemaList(response) {
        var oArray = response.data;
        var pArray = [];
        this.schema = [];

        for (var c = 0; c < oArray.length; c++) {
            pArray.push(this.ajaxWrapper.get(this.getSchemaColumnPropertiesBaseUrl() + '/' + oArray[c], null));
        }
        return Promise.all(pArray);
    }
    private processSchemaData(response) {
        this.schema = [];
        for (let c = 0; c < response.length; c++) {
            response[c].data.columnIndex = c;
            this.schema.push(new SchemaColumn(response[c].data));
        }
        return this.schema;
    }
    /**
    * Gets a configured query helper for this overlay
    * @function
    * @export
    * @returns {CQueryHelper} The new query helper object
    */
    public getQueryHelper(): IQueryHelper {
        let helper = null;
        const url = this.getQueryUrl();
        const nextUrl = this.getNextResultsUrls();
        if (this.properties['WML::HasJoin&'] && this.properties['WML::HasJoin&'] === 1) {
            //helper = new Cadcorp.Web.Entities.GeognoSIS.JoinedOverlayQueryHelper({ overlay: this });
            helper = new JoinedOverlayQueryHelper(this, this.ajaxWrapper, url, nextUrl, this.epsgCode);
        } else {
            helper = new QueryHelper({ sessionId: this.sessionId, index: this.overlayIndex, name: this.name }, this.ajaxWrapper, url, nextUrl, this.epsgCode, this.mapName);
        }
        return helper;
    }
    /**
    * Clears all the features in this overlay
    * @function
    * @export
    * @returns {Promise} The chainable promise
    */
    clearFeatures() {
        const url = this.getItemsUrl();
        const promise = this.ajaxWrapper.delete(url, null);
        return promise;
    }
    /**
    * Gets the themes for this overlay
    * @function
    * @export
    * @returns {Promise} The chainable promise
    */
    public getThemes(): Promise<ITheme[]> {
        let retVal: Promise<ITheme[]> = null;
        if (this.m_themes) {
            retVal = Promise.resolve(this.m_themes);
        }
        else {
            const url = this.getThemeBaseUrl();
            retVal = this.ajaxWrapper.get(url, null)
                .then((response) => {
                    if (response.data && response.data.length > 0) {
                        this.m_themes = [];
                        for (let count = 0; count < response.data.length; count++) {
                            const data = response.data[count];
                            const theme = new Theme(this.baseUrl, this.sessionId, this.overlayIndex, this.ajaxWrapper, this.layerId, this.mapName);
                            theme.on('changed', (eventArgs) => {
                                this.m_events.dispatchEvent('themeChanged', eventArgs);
                                return true;
                            });
                            theme.index = data.index;
                            theme.disabled = data.disabled;
                            theme.title = data.title;
                            theme.type = data.type;
                            this.m_themes.push(theme);
                        }
                        this.m_themes.sort((first: ITheme, second: ITheme) => {
                            return first.index - second.index;
                        });
                    } else
                        this.m_themes = [];
                    return this.m_themes;
                });
        }

        return retVal;
    }
    /**
    * Creates a new theme on this overlay
    * @function
    * @param {INewTheme} addTheme The new theme to add
    * @export
    * @returns {Promise<ITheme>} The chainable promise
    */
    public addTheme(newTheme: INewTheme): Promise<ITheme> {
        const url = this.getThemeBaseUrl();
        return this.getThemes()//in case the themes haven't been retrieved yet
            .then((themes) => {
                return this.ajaxWrapper.post(url, newTheme, null);
            })
            .then((response) => {
                let addedTheme = null as ITheme;
                if (response.data) {
                    const data = response.data;
                    addedTheme = this.createTheme(data, newTheme);

                    this.m_themes.push(addedTheme);
                }
                return addedTheme;
            });
    }
    /**
    * removes new theme from this overlay
    * @function
    * @param {ITheme} aTheme The theme to remove
    * @export
    * @returns {Promise<ITheme>} The chainable promise
    */
    public removeTheme(aTheme: ITheme): Promise<ITheme> {
        return this.getThemes() //in case the themes haven't been retrieved yet
            .then(() => {
                const url = `${this.getThemeBaseUrl()}/${aTheme.index}`;
                return this.ajaxWrapper.delete(url);
            }).then((response) => {
                const remaining = this.m_themes.filter((themeElement: ITheme) => {
                    return themeElement.index !== aTheme.index;
                });
                this.m_themes = remaining;
                //now adjust the index of any themes after the removed one
                if (aTheme.index > this.m_themes.length) {
                    for (let count = aTheme.index; count < this.m_themes.length; count++) {
                        this.m_themes[count].index--;
                        if (this.m_themes[count].definition) {
                            this.m_themes[count].definition.index = this.m_themes[count].index;
                        }
                    }
                }
                return aTheme;
            });
    }
    /**
    * Updates a theme on this overlay
    * @function
    * @param {INewTheme} theme The theme to update
    * @export
    * @returns {Promise<ITheme>} The chainable promise
    */
    public updateTheme(theme: INewTheme): Promise<ITheme> {
        const url = `${this.getThemeBaseUrl()}/${theme.index}`;
        let themes: ITheme[] = null;
        return this.getThemes()//in case the themes haven't been retrieved yet
            .then((foundThemes) => {
                themes = foundThemes;
                return this.ajaxWrapper.patch(url, theme, null);
            })
            .then((response) => {
                let newTheme = null as ITheme;
                if (response.data) {
                    const data = response.data;
                    const updatedTheme = this.createTheme(data, theme);
                    let index = 0;
                    var found = this.m_themes.filter((element: ITheme, filterIndex: number, array: ITheme[]) => {
                        if (element.index === updatedTheme.index)
                            index = filterIndex;
                        return element.index === updatedTheme.index;
                    });
                    newTheme = updatedTheme;
                    this.m_themes[index] = updatedTheme;
                }
                return newTheme;
            });
    }
    private createTheme(themeData: any, definition: INewTheme): Theme {
        const theme = new Theme(this.baseUrl,
            this.sessionId,
            this.overlayIndex,
            this.ajaxWrapper,
            this.layerId,
            this.mapName);
        theme.userAdded = true;
        theme.on('changed',
            (eventArgs) => {
                this.m_events.dispatchEvent('themeChanged', eventArgs);
                return true;
            });
        theme.index = themeData.index;
        theme.disabled = themeData.disabled;
        theme.title = themeData.title;
        theme.type = themeData.type;
        definition.index = themeData.index;
        theme.definition = definition;
        return theme
    }
    public visibleAtScale(scale: number): boolean {
        var visible = false;
        if (this.scaleMin !== 0) {
            visible = this.scaleMin <= scale;
        }
        else
            visible = true;
        if (this.scaleMax !== 0) {
            visible = visible && (this.scaleMax >= scale);
        }

        return visible;
    }
}
export enum LayerStatus {
    Invisible = 0,
    Visible,
    Hittable,
    Editable
}


